import React, { useEffect } from "react";
import SectionHeader from "@/components/common/SectionHeader";
import { Package, Box, Container, MapPin } from "lucide-react";

const PACKAGING = [
  { title: "PP Bags", body: "Polypropylene woven bags — standard for grains and pulses. Common sizes: 5, 10, 25, 50 kg." },
  { title: "BOPP Bags", body: "Bi-axially oriented polypropylene bags with high-quality printing for premium retail-ready packs." },
  { title: "Jute Bags", body: "Natural fibre bags for select commodities and buyers with sustainability preferences." },
  { title: "Custom-Labelled Bags", body: "Buyer-branded packaging with private-label artwork coordinated with packaging partners." },
  { title: "Bulk Packaging", body: "Jumbo/FIBC bags and bulk container loading for high-volume requirements." },
  { title: "Buyer-Specific Packaging", body: "Any bespoke bag, size, liner or labelling requirement can be arranged." },
];

const DETAILS = [
  { label: "Bag Size Options", value: "5 kg · 10 kg · 25 kg · 50 kg · Jumbo bags — buyer-configurable" },
  { label: "Labelling", value: "Standard, private-label, multilingual and destination-compliant labelling" },
  { label: "Container Loading", value: "20ft FCL / 40ft FCL — bag, pallet or bulk-loaded" },
  { label: "Palletisation", value: "Pallets with stretch wrap and corner protection on request" },
  { label: "Destination Ports", value: "Coordinated based on buyer preference and commodity" },
  { label: "Delivery Terms", value: "FOB · CIF · CFR · Ex-Works — subject to negotiation" },
  { label: "Shipment Schedule", value: "Confirmed after order acceptance, sample approval and payment terms" },
];

export default function LogisticsPage() {
  useEffect(() => { document.title = "Packaging & Logistics — Aaha Kanakam Traders"; }, []);
  return (
    <div data-testid="logistics-page">
      <section className="bg-forest text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="eyebrow text-gold mb-3">Packaging & Logistics</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">Flexible packaging. Coordinated logistics.</h1>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeader eyebrow="Packaging Formats" title="Packaging options for every commodity and market" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PACKAGING.map((p, i) => (
              <div key={p.title} data-testid={`packaging-${i}`} className="p-7 bg-white border border-borderSubtle card-hover">
                <Package size={22} className="text-gold-600 mb-4" />
                <div className="font-serif text-xl text-forest mb-2">{p.title}</div>
                <p className="text-sm text-slateneutral leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <SectionHeader eyebrow="Logistics Details" title="All shipment details are buyer-configurable" />
          <div className="bg-white border border-borderSubtle">
            {DETAILS.map((d, i) => (
              <div key={d.label} className={`grid md:grid-cols-12 gap-4 p-5 ${i > 0 ? "border-t border-borderSubtle" : ""}`}>
                <div className="md:col-span-4 eyebrow">{d.label}</div>
                <div className="md:col-span-8 text-sm text-charcoal">{d.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
